import React, { useEffect, useState } from 'react';
import styles from './PagesPage.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  };
}

const PAGE_ICONS = {
  about_us: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  contact_us: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  privacy_policy: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  terms: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
};

const PAGE_LIST = [
  { slug: 'about_us',       label: 'About Us' },
  { slug: 'contact_us',     label: 'Contact Us' },
  { slug: 'privacy_policy', label: 'Privacy Policy' },
  { slug: 'terms',          label: 'Terms & Conditions' },
];

export default function PagesPage() {
  const [pages, setPages]         = useState({});
  const [activeSlug, setActive]   = useState('about_us');
  const [title, setTitle]         = useState('');
  const [content, setContent]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res  = await fetch(`${API}/admin/pages`, { headers: authHeaders() });
      const data = await res.json();
      const map  = {};
      (data.pages || []).forEach(p => { map[p.slug] = p; });
      setPages(map);
      selectPage('about_us', map);
    } catch { /* ignore */ }
  }

  function selectPage(slug, map = pages) {
    setActive(slug);
    setMsg('');
    const p = map[slug];
    setTitle(p?.title   || '');
    setContent(p?.content || '');
  }

  async function save() {
    setSaving(true);
    setMsg('');
    try {
      const res = await fetch(`${API}/admin/pages/${activeSlug}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ title, content }),
      });
      const data = await res.json();
      setPages(prev => ({ ...prev, [activeSlug]: data.page }));
      setMsg('Saved successfully!');
    } catch {
      setMsg('Failed to save. Try again.');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  }

  const activeMeta = PAGE_LIST.find(p => p.slug === activeSlug);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>App Pages</h1>
        <p className={styles.subtitle}>Edit content shown in the mobile app — changes appear instantly.</p>
      </div>

      <div className={styles.layout}>
        {/* Sidebar — page list */}
        <div className={styles.sidebar}>
          {PAGE_LIST.map(p => (
            <button
              key={p.slug}
              className={`${styles.pageTab} ${activeSlug === p.slug ? styles.pageTabActive : ''}`}
              onClick={() => selectPage(p.slug)}
            >
              <span className={styles.pageTabIcon}>{PAGE_ICONS[p.slug]}</span>
              <span className={styles.pageTabLabel}>{p.label}</span>
              {pages[p.slug] && (
                <span className={styles.pageTabDate}>
                  {new Date(pages[p.slug].updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className={styles.editor}>
          <div className={styles.editorHeader}>
            <span className={styles.editorIcon}>{PAGE_ICONS[activeSlug]}</span>
            <h2 className={styles.editorTitle}>{activeMeta?.label}</h2>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Page Title</label>
            <input
              className={styles.input}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Page title shown in the app"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Content
              <span className={styles.labelHint}> — supports **bold**, ## Heading, ### Sub-heading, - bullet points</span>
            </label>
            <textarea
              className={styles.textarea}
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write content here..."
              rows={28}
            />
          </div>

          <div className={styles.editorFooter}>
            {msg && (
              <span className={msg.includes('success') ? styles.msgOk : styles.msgErr}>{msg}</span>
            )}
            <button className={styles.btn} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          {/* Live preview */}
          <div className={styles.previewSection}>
            <h3 className={styles.previewTitle}>Preview</h3>
            <div className={styles.previewCard}>
              <p className={styles.previewHeading}>{title}</p>
              {content.split('\n').map((line, i) => {
                const t = line.trim();
                if (t.startsWith('### '))
                  return <p key={i} className={styles.previewH3}>{t.slice(4)}</p>;
                if (t.startsWith('## '))
                  return <p key={i} className={styles.previewH2}>{t.slice(3)}</p>;
                if (t.startsWith('- ') || t.startsWith('• '))
                  return <p key={i} className={styles.previewBullet}>• {t.slice(2)}</p>;
                if (t === '')
                  return <div key={i} style={{ height: 6 }} />;
                return (
                  <p key={i} className={styles.previewP}
                    dangerouslySetInnerHTML={{ __html: t.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
